import React, { useEffect, useRef, useState } from 'react';

const SWIFTMAZE_WHATSAPP_NUMBER = '919978623133';
const emptyForm = { name: '', businessName: '', phone: '', email: '', websiteUrl: '', mainConcern: '' };
const offerTitles = {
  'Free Website Audit': 'Request Your Free Website Audit',
  'Free SEO Report': 'Get Your Free SEO Report',
  'Free CRM Demo': 'Book Your Free CRM Demo',
};

const FreeReviewModal = ({ selectedOffer, onClose }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!selectedOffer) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleEscape = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, selectedOffer]);

  if (!selectedOffer) return null;

  const handleChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!formData.businessName.trim()) nextErrors.businessName = 'Please enter your business name.';
    if (!formData.phone.trim()) nextErrors.phone = 'Please enter your phone or WhatsApp number.';
    if (!formData.email.trim()) nextErrors.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) nextErrors.email = 'Please enter a valid email address.';
    if (!formData.mainConcern.trim()) nextErrors.mainConcern = 'Please describe your main concern.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    const message = `Hello SwiftMaze Team,

I am interested in: ${selectedOffer}

Name: ${formData.name.trim()}
Business Name: ${formData.businessName.trim()}
Phone / WhatsApp: ${formData.phone.trim()}
Email: ${formData.email.trim()}
Website URL: ${formData.websiteUrl.trim() || 'Not Provided'}
Main Concern: ${formData.mainConcern.trim()}

Please review my details and contact me regarding this request.`;
    window.open(`https://wa.me/${SWIFTMAZE_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const renderField = (name, label, type = 'text', options = {}) => (
    <div className={`form-group ${options.full ? 'col-lg-12' : 'col-lg-6'} col-md-6 col-sm-12`}>
      <label htmlFor={`free-review-${name}`}>{label}{options.required && ' *'}</label>
      <input id={`free-review-${name}`} name={name} type={type} value={formData[name]} onChange={handleChange}
        placeholder={options.placeholder} autoComplete={options.autoComplete} aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `free-review-${name}-error` : undefined} />
      {errors[name] && <div className="free-review-field-error" id={`free-review-${name}-error`}>{errors[name]}</div>}
    </div>
  );

  return (
    <div className="inquiry-popup-overlay free-review-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="inquiry-popup-dialog free-review-dialog" role="dialog" aria-modal="true" aria-labelledby="free-review-title">
        <button ref={closeButtonRef} type="button" className="inquiry-popup-close" onClick={onClose} aria-label="Close free business review form">
          <span className="fa fa-times" aria-hidden="true"></span>
        </button>
        <div className="inquiry-popup-head">
          <span className="inquiry-popup-kicker">Complimentary Business Review</span>
          <h3 id="free-review-title">{offerTitles[selectedOffer]}</h3>
          <p>Share a few details. WhatsApp will open with your request ready for you to send.</p>
        </div>
        <div className="contact-form inquiry-popup-form">
          <form onSubmit={handleSubmit} noValidate>
            <input type="hidden" name="selectedOffer" value={selectedOffer} />
            <div className="row clearfix">
              {renderField('name', 'Name', 'text', { required: true, placeholder: 'Your name', autoComplete: 'name' })}
              {renderField('businessName', 'Business Name', 'text', { required: true, placeholder: 'Business or organization' })}
              {renderField('phone', 'Phone / WhatsApp', 'tel', { required: true, placeholder: 'Your contact number', autoComplete: 'tel' })}
              {renderField('email', 'Email', 'email', { required: true, placeholder: 'you@example.com', autoComplete: 'email' })}
              {renderField('websiteUrl', 'Website URL', 'url', { full: true, placeholder: 'https://example.com (optional)', autoComplete: 'url' })}
              <div className="form-group col-lg-12 col-md-12 col-sm-12">
                <label htmlFor="free-review-mainConcern">Main Concern *</label>
                <textarea id="free-review-mainConcern" name="mainConcern" value={formData.mainConcern} onChange={handleChange}
                  placeholder="Tell us what you would like help with" aria-invalid={Boolean(errors.mainConcern)}
                  aria-describedby={errors.mainConcern ? 'free-review-mainConcern-error' : undefined}></textarea>
                {errors.mainConcern && <div className="free-review-field-error" id="free-review-mainConcern-error">{errors.mainConcern}</div>}
              </div>
              <div className="form-group text-center col-lg-12 col-md-12 col-sm-12">
                <button className="theme-btn btn-style-three" type="submit"><span className="txt">Continue to WhatsApp</span></button>
                <p className="free-review-send-note">You will send the prepared message manually in WhatsApp.</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FreeReviewModal;
